import { Dispatch, FC, SyntheticEvent, forwardRef, useCallback, useState } from "react";
import {
  Button, Dialog, DialogActions, DialogContent, DialogContentText,
  DialogTitle, Slide, Stack, TableCell, TableRow, tableCellClasses,
  styled, Accordion, AccordionSummary, List, ListItem, ListItemText,
  Typography, Box, Divider, AccordionDetails, Card, CardContent,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import BorderColorIcon from "@mui/icons-material/BorderColor";
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExitToAppIcon from "@mui/icons-material/ExitToApp";
import { btnStyle } from "../parametrage/user/Tabs/utilisateur/UtilisateurRow";
import { AxiosInstance } from "axios";
import {
  profilToAccessFromSaisieManager, AllMounth,
  profilAllAccessBudgetSiVerouiller, PROFIL_SUPER_ADMIN
} from "../../../constants/Constants";
import { ConnectedUser } from "../../../utils/utils";
import { TransitionProps } from "@mui/material/transitions";
import { LoadingButton } from "@mui/lab";
import { BudgetData } from "../../../types/DataBudget";
import OptionalWidthModal from "../../../commons/optional-width-modal/OptionalWidthModal";
import UpdateBudgetForm from "../../forms/Budget/UpdateBudgetForm";
import { AlertType } from "../../../types/AlertType";
import { profilsCanManageBudget } from "./Budget";
import { IYearsVerouillageClotureLocked } from "./ColumnHeader";
import DuplicateBudgetForm from "../../forms/Budget/DuplicateBudgetForm";
import AccessTimeIcon from '@mui/icons-material/AccessTime';

export const StyledTableCell = styled(TableCell)(() => ({
  [`&.${tableCellClasses.body}`]: {
    fontSize: 11,
    textAlign: "center",
    color: "#fff",
  },
}));

export const Transition = forwardRef(function Transition(
  props: TransitionProps & {
    children: React.ReactElement<any, any>;
  },
  ref: React.Ref<unknown>
) {
  return <Slide direction="up" ref={ref} {...props} />;
});

interface BudgetRowProps {
  budget: BudgetData;
  http: AxiosInstance;
  model: string;
  expanded: number | false;
  isDesktopOrLaptop: boolean;
  setExpanded: Dispatch<React.SetStateAction<number | false>>;
  setOpenSnack: Dispatch<React.SetStateAction<boolean>>;
  setAlerteMessage: Dispatch<React.SetStateAction<string>>;
  setAlertType: Dispatch<React.SetStateAction<AlertType>>;
  reloadList: () => void;
  dateVerrouillageCloture: IYearsVerouillageClotureLocked
}

const BudgetRow: FC<BudgetRowProps> = ({
  budget,
  http,
  model,
  expanded,
  isDesktopOrLaptop,
  setExpanded,
  setOpenSnack,
  setAlerteMessage,
  setAlertType,
  reloadList,
  dateVerrouillageCloture
}) => {
  const user = ConnectedUser();
  const bgColorEditBtn = profilToAccessFromSaisieManager.includes(
    user?.profils?.libelle
  )
    ? "#070d23"
    : "#1A168C";

  const [openModal, setOpenModal] = useState<boolean>(false);
  const [openModalDelete, setOpenModalDelete] = useState<boolean>(false);
  const [openDuplicateModal, setOpenDuplicateModal] = useState<boolean>(false);
  const [deleteLoading, setDeleteLoading] = useState<boolean>(false);

  const handleClose = () => {
    setOpenModal(false);
  };

  const handleCloseDeleteModal = () => {
    setOpenModalDelete(false);
  };

  const handleCloseDuplicateModal = () => {
    setOpenDuplicateModal(false);
  };

  const handleDelete = useCallback(() => {
    setDeleteLoading(true);
    http.delete(`${model}/delete/${budget?.id}`).then(() => {
      setOpenModalDelete(false);
      setDeleteLoading(false);
      reloadList();
      setAlerteMessage("Suppresion efféctuée !");
      setAlertType("success");
      setOpenSnack(true);
      reloadList();
    }).catch(error => {
      console.log(error);
    });
  }, []);

  const handleChangeAccordionValue = (panel: number) => (event: SyntheticEvent, isExpanded: boolean) => {
    setExpanded(isExpanded ? panel : false);
  };

  const disableDeleteBtn = (): boolean => {
    /**
     * Le bouton “Supprimer” est grisé si article est prod (is_budget = 0) et profil user connecté est DRC
     */
    if (profilsCanManageBudget.includes(user?.profils?.libelle) && (budget.article_budget !== 1)) {
      return true;
    }

    if (!profilToAccessFromSaisieManager.includes(user?.profils?.libelle) ||
      budget?.article_actif === 0) {
      return true;
    }

    // Si Profil n'est pas super Admin et le Budget est Cloturé
    if ((![PROFIL_SUPER_ADMIN].includes(user?.profils?.libelle)
      && dateVerrouillageCloture.YearsClotureBudget.includes(budget?.annee))) {
      return true;
    }

    // Si Profil n'est Pas ADV, Super Admin on block Si Le Budget est Verouillé Ou Budget n'est Pas Actif
    if ((!profilAllAccessBudgetSiVerouiller.includes(user?.profils?.libelle)
      && dateVerrouillageCloture?.YearsVerouillageBudget.includes(budget?.annee))) {
      return true;
    } else {
      /**
      * Le bouton “Modifier” est grisé si article est prod (is_budget = 0) et profil user connecté est DRC
      */
      if (profilsCanManageBudget.includes(user?.profils?.libelle) && (budget.article_budget !== 1))
        return true;
    }

    return false;
  };

  const disableEditBtn = (): boolean => {
    // Si L'article n'est Pas Actif
    if (budget?.article_actif === 0) return true;

    // Si Profil n'est pas super Admin et le Budget est Cloturé
    if ((![PROFIL_SUPER_ADMIN].includes(user?.profils?.libelle)
      && dateVerrouillageCloture.YearsClotureBudget.includes(budget?.annee))) {
      return true;
    }

    // Si Profil n'est Pas ADV, Super Admin on block Si Le Budget est Verouillé Ou Budget n'est Pas Actif
    if ((!profilAllAccessBudgetSiVerouiller.includes(user?.profils?.libelle)
      && dateVerrouillageCloture?.YearsVerouillageBudget.includes(budget?.annee))) {
      return true;
    } else {
      /**
      * Le bouton “Modifier” est grisé si article est prod (is_budget = 0) et profil user connecté est DRC
      */
      if (profilsCanManageBudget.includes(user?.profils?.libelle) && (budget.article_budget !== 1))
        return true;
    }

    return false;
  }

  let addTitle = "Modifier un Budget";
  if (model === "Reforcaste") {
    addTitle = "Modification Reforcaste";
  }

  const deleteMessageConfirmation = ` Vous êtes sur le point de supprimer ce ${model}, êtes-vous sûr de
  vouloir poursuivre cette action ?`;

  const clientValue = () => {
    if (budget?.client_libelle) {
      return budget?.client_libelle;
    }

    if (budget?.client_budget_libelle) {
      return budget?.client_budget_libelle;
    }

    if (budget?.client_prod_for_article_budget_libelle) {
      return budget?.client_prod_for_article_budget_libelle;
    }

    return null;
  };

  const operationValue = () => {
    if (budget?.operation_libelle) {
      return budget?.operation_libelle;
    }

    if (budget?.operation_budget_libelle) {
      return budget?.operation_budget_libelle;
    }

    return null;
  };

  return (
    <>
      {isDesktopOrLaptop ? (
        <TableRow>
          <StyledTableCell>
            {budget?.pays_libelle}
          </StyledTableCell>
          <StyledTableCell>
            {budget?.bu_libelle}
          </StyledTableCell>
          <StyledTableCell>
            {clientValue()}
          </StyledTableCell>
          <StyledTableCell>
            {operationValue()}
          </StyledTableCell>
          <StyledTableCell>
            {budget?.code_article_libelle}
          </StyledTableCell>
          <StyledTableCell>
            {budget?.type_libelle}
          </StyledTableCell>
          <StyledTableCell>
            {budget?.article_libelle}
          </StyledTableCell>
          <StyledTableCell>
            {AllMounth.find((x) => x.id === budget?.mois)?.mounth}
          </StyledTableCell>
          <StyledTableCell>
            {budget?.annee}
          </StyledTableCell>
          <StyledTableCell>
            {budget?.ca_budget && parseFloat(budget?.ca_budget)?.toFixed(2)}
          </StyledTableCell>
          <StyledTableCell>
            {budget?.tx_marge_budget && parseFloat(budget?.tx_marge_budget)?.toFixed(2)}
          </StyledTableCell>
          <StyledTableCell>
            {budget?.marge_valeur_budget && parseFloat(budget?.marge_valeur_budget)?.toFixed(2)}
          </StyledTableCell>
          <StyledTableCell>
            {budget?.qte_heure_budget && parseFloat(budget?.qte_heure_budget)?.toFixed(2)}
          </StyledTableCell>
          <StyledTableCell>
            {budget?.qte_acte_budget && parseFloat(budget?.qte_acte_budget)?.toFixed(2)}
          </StyledTableCell>
          <StyledTableCell>
            {budget?.dmt ? (
              <Box display="flex" alignItems="center" justifyContent="center" gap={0.5}>
                <AccessTimeIcon sx={{ fontSize: '1rem' }} />
                <span>{budget?.dmt}</span>
              </Box>
            ) : ""}
          </StyledTableCell>
          <StyledTableCell>
            {budget?.taux_occupation !== null && budget?.taux_occupation !== undefined ? `${budget?.taux_occupation} %` : ""}
          </StyledTableCell>
          <StyledTableCell>
            {budget?.taux_conges !== null && budget?.taux_conges !== undefined ? `${budget?.taux_conges} %` : ""}
          </StyledTableCell>
          <StyledTableCell>
            {budget?.taux_absenteisme !== null && budget?.taux_absenteisme !== undefined ? `${budget?.taux_absenteisme} %` : ""}
          </StyledTableCell>
          {/*<StyledTableCell>
            {budget?.heure_cout_budget && parseFloat(String(budget?.heure_cout_budget))?.toFixed(2)}
          </StyledTableCell>*/}
          <StyledTableCell>
            <Stack
              direction="row"
              spacing={1}
              display="flex"
              justifyContent="center"
            >
              <Button
                size="small"
                variant="outlined"
                sx={btnStyle({
                  bgColor: bgColorEditBtn,
                  hoverColor: "Gray",
                  color: "#fff",
                })}
                startIcon={<BorderColorIcon />}
                onClick={() => setOpenModal(true)}
                disabled={disableEditBtn()}
              >
                Modifier
              </Button>
              <Button
                size="small"
                variant="outlined"
                sx={btnStyle({
                  bgColor: "#FF0000",
                  hoverColor: "#FE3737",
                })}
                disabled={disableDeleteBtn()}
                startIcon={<DeleteIcon />}
                onClick={() => setOpenModalDelete(true)}
              >
                Supprimer
              </Button>

              <Button
                size="small"
                color="warning"
                variant="contained"
                startIcon={<ContentCopyIcon />}
                onClick={() => setOpenDuplicateModal(true)}
                sx={{ textTransform: 'none' }}
              >
                Dupliquer
              </Button>
            </Stack>
          </StyledTableCell>
        </TableRow>
      ) : (
        <Accordion
          expanded={expanded === budget?.id}
          onChange={handleChangeAccordionValue(budget?.id as number)}
          sx={{
            borderRadius: 1,
            bgcolor: "#fff",
            mb: 1,
            ml: 0.1
          }}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon sx={{ color: "#080d24" }} />}
            aria-controls="panel1-content"
            id="panel1-header"
          >
            <Stack direction="column">
              <List sx={{ py: 0 }} dense>
                <ListItem sx={{ my: 0, py: 0 }} dense>
                  <ListItemText
                    primary="Désignation Article:"
                    secondary={
                      <Typography sx={{ fontSize: 12, color: "#FC8404" }}>
                        {budget?.article_libelle}
                      </Typography>
                    }
                  />
                </ListItem>
                <ListItem sx={{ my: 0, py: 0 }} dense>
                  <ListItemText
                    primary="Code Article :"
                    secondary={
                      <Typography sx={{ fontSize: 12, color: "#FC8404" }}>
                        {budget?.code_article_libelle}
                      </Typography>
                    }
                  />
                </ListItem>
                <ListItem sx={{ my: 0, py: 0 }} dense>
                  <ListItemText
                    primary="Mois :"
                    secondary={
                      <Typography sx={{ fontSize: 12, color: "#FC8404" }}>
                        {AllMounth.find((x) => x.id === budget?.mois)?.mounth}
                      </Typography>
                    }
                  />
                </ListItem>
              </List>
            </Stack>
          </AccordionSummary>
          <AccordionDetails>
            <Stack direction="column" spacing={1}>
              <Button
                variant="contained"
                sx={btnStyle({
                  bgColor: "Gray",
                  hoverColor: "Gray",
                  color: "#fff",
                })}
                size="small"
                fullWidth
                disabled={budget?.article_actif === 0}
                startIcon={<BorderColorIcon />}
                onClick={() => setOpenModal(true)}
              >
                Modifier
              </Button>
              <Button
                size="small"
                variant="contained"
                sx={btnStyle({
                  bgColor: "#FF0000",
                  hoverColor: "#FE3737",
                })}
                fullWidth
                disabled={disableDeleteBtn()}
                startIcon={<DeleteIcon />}
                onClick={() => setOpenModalDelete(true)}
              >
                Supprimer
              </Button>

              <Button
                size="small"
                color="warning"
                variant="contained"
                fullWidth
                startIcon={<ContentCopyIcon />}
                onClick={() => setOpenDuplicateModal(true)}
                sx={{ textTransform: 'none' }}
              >
                Dupliquer
              </Button>

              <Box mt={5}>
                <Typography
                  gutterBottom
                  variant="h5"
                  component="div"
                  sx={{ mb: 0 }}
                >
                  Détails
                </Typography>
                <Divider
                  orientation="horizontal"
                  sx={{ bgcolor: "#fff", mb: 1 }}
                />
              </Box>

              <Card>
                <CardContent>
                  <List dense>
                    <ListItem dense>
                      <ListItemText
                        primary="Pays :"
                        secondary={budget?.pays_libelle}
                      />
                    </ListItem>

                    <Divider
                      orientation="horizontal"
                      sx={{ bgcolor: " #dfdfdf" }}
                    />
                    <ListItem dense>
                      <ListItemText
                        primary="Bu :"
                        secondary={budget?.bu_libelle}
                      />
                    </ListItem>

                    <Divider
                      orientation="horizontal"
                      sx={{ bgcolor: " #dfdfdf" }}
                    />
                    <ListItem dense>
                      <ListItemText
                        primary="Client :"
                        secondary={budget?.client_libelle}
                      />
                    </ListItem>

                    <Divider
                      orientation="horizontal"
                      sx={{ bgcolor: " #dfdfdf" }}
                    />
                    <ListItem dense>
                      <ListItemText
                        primary="Opération :"
                        secondary={budget?.operation_libelle}
                      />
                    </ListItem>

                    {budget?.ca_budget && (
                      <>
                        <Divider
                          orientation="horizontal"
                          sx={{ bgcolor: " #dfdfdf" }}
                        />
                        <ListItem dense>
                          <ListItemText
                            primary="CA_Budget :"
                            secondary={parseFloat(budget?.ca_budget)?.toFixed(2)}
                          />
                        </ListItem>
                      </>
                    )}

                    {budget?.tx_marge_budget && (
                      <>
                        <Divider
                          orientation="horizontal"
                          sx={{ bgcolor: " #dfdfdf" }}
                        />
                        <ListItem dense>
                          <ListItemText
                            primary="Taux_Marge_budget :"
                            secondary={parseFloat(budget?.tx_marge_budget)?.toFixed(2)}
                          />
                        </ListItem>
                      </>
                    )}

                    {budget?.marge_valeur_budget && (
                      <>
                        <Divider
                          orientation="horizontal"
                          sx={{ bgcolor: " #dfdfdf" }}
                        />
                        <ListItem dense>
                          <ListItemText
                            primary="Marge_Valeur_budget :"
                            secondary={parseFloat(budget?.marge_valeur_budget)?.toFixed(2)}
                          />
                        </ListItem>
                      </>
                    )}

                    {budget?.qte_acte_budget && (
                      <>
                        <Divider
                          orientation="horizontal"
                          sx={{ bgcolor: " #dfdfdf" }}
                        />
                        <ListItem dense>
                          <ListItemText
                            primary="Acte Budget :"
                            secondary={parseFloat(budget?.qte_acte_budget)?.toFixed(2)}
                          />
                        </ListItem>
                      </>
                    )}

                    {budget?.qte_heure_budget && (
                      <>
                        <Divider
                          orientation="horizontal"
                          sx={{ bgcolor: " #dfdfdf" }}
                        />
                        <ListItem dense>
                          <ListItemText
                            primary="Heure Budget :"
                            secondary={parseFloat(budget?.qte_heure_budget)?.toFixed(2)}
                          />
                        </ListItem>
                      </>
                    )}

                    {budget?.heure_cout_budget && (
                      <>
                        <Divider
                          orientation="horizontal"
                          sx={{ bgcolor: " #dfdfdf" }}
                        />
                        <ListItem dense>
                          <ListItemText
                            primary="Heure coût budget :"
                            secondary={parseFloat(String(budget?.heure_cout_budget))?.toFixed(2)}
                          />
                        </ListItem>
                      </>
                    )}

                    {budget?.dmt && (
                      <>
                        <Divider
                          orientation="horizontal"
                          sx={{ bgcolor: " #dfdfdf" }}
                        />
                        <ListItem dense>
                          <ListItemText
                            primary="DMT :"
                            secondary={
                              <Box display="flex" alignItems="center" gap={0.5} sx={{ color: 'text.secondary', mt: 0.5 }}>
                                <AccessTimeIcon sx={{ fontSize: '1rem' }} />
                                <span>{budget?.dmt}</span>
                              </Box>
                            }
                          />
                        </ListItem>
                      </>
                    )}

                    {budget?.taux_occupation !== null && budget?.taux_occupation !== undefined && (
                      <>
                        <Divider
                          orientation="horizontal"
                          sx={{ bgcolor: " #dfdfdf" }}
                        />
                        <ListItem dense>
                          <ListItemText
                            primary="Taux occupation :"
                            secondary={`${budget?.taux_occupation} %`}
                          />
                        </ListItem>
                      </>
                    )}

                    {budget?.taux_conges !== null && budget?.taux_conges !== undefined && (
                      <>
                        <Divider
                          orientation="horizontal"
                          sx={{ bgcolor: " #dfdfdf" }}
                        />
                        <ListItem dense>
                          <ListItemText
                            primary="Taux congés :"
                            secondary={`${budget?.taux_conges} %`}
                          />
                        </ListItem>
                      </>
                    )}

                    {budget?.taux_absenteisme !== null && budget?.taux_absenteisme !== undefined && (
                      <>
                        <Divider
                          orientation="horizontal"
                          sx={{ bgcolor: " #dfdfdf" }}
                        />
                        <ListItem dense>
                          <ListItemText
                            primary="Taux absentéisme :"
                            secondary={`${budget?.taux_absenteisme} %`}
                          />
                        </ListItem>
                      </>
                    )}
                  </List>
                </CardContent>
              </Card>
            </Stack>
          </AccordionDetails>
        </Accordion>
      )}

      <Dialog
        open={openModalDelete}
        TransitionComponent={Transition}
        keepMounted
        onClose={handleCloseDeleteModal}
        aria-describedby="alert-dialog-slide-description"
      >
        <DialogTitle>{"Confirmation"}</DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-slide-description">
            {deleteMessageConfirmation}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <LoadingButton
            onClick={() => handleDelete()}
            startIcon={<DeleteIcon />}
            loading={deleteLoading}
            loadingPosition="end"
            sx={btnStyle({
              bgColor: "#FF0000",
              hoverColor: "#FE3737",
            })}
            variant="contained"
          >
            Oui
          </LoadingButton>
          <LoadingButton
            onClick={() => handleCloseDeleteModal()}
            startIcon={<ExitToAppIcon />}
            sx={{ textTransform: "none" }}
            loadingPosition="end"
            variant="contained"
            color="inherit"
          >
            Non
          </LoadingButton>
        </DialogActions>
      </Dialog>

      <OptionalWidthModal
        title={addTitle}
        onClose={handleClose}
        open={openModal}
        breackPoint="md"
      >
        <UpdateBudgetForm
          budget={budget}
          http={http}
          model={model}
          profil={user?.profils?.libelle}
          onClose={handleClose}
          setOpenSnack={setOpenSnack}
          setAlerteMessage={setAlerteMessage}
          setAlertType={setAlertType}
          reloadList={reloadList}
        />
      </OptionalWidthModal>

      <OptionalWidthModal
        title="Dupliquer un budget"
        onClose={handleCloseDuplicateModal}
        open={openDuplicateModal}
        breackPoint="md"
      >
        <DuplicateBudgetForm
          budget={budget}
          http={http}
          model={model}
          profil={user?.profils?.libelle}
          onClose={handleCloseDuplicateModal}
          setOpenSnack={setOpenSnack}
          setAlerteMessage={setAlerteMessage}
          setAlertType={setAlertType}
          reloadList={reloadList}
        />
      </OptionalWidthModal>
    </>
  );
};

export default BudgetRow;
